/**
 * ImagePasteArea Component
 * Handles clipboard paste for multiple images
 * Uploads to Supabase Storage and displays thumbnails
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Upload } from 'lucide-react';
import SupabaseStorageService from '../../services/supabaseStorageService';
import FilePreviewModalSimple from '../FileUpload/FilePreviewModalSimple';

const ImagePasteArea = ({ images = [], onImagesChange, projectId, clientId, disabled = false, type = 'project' }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const pasteAreaRef = useRef(null);

  const uploadImages = useCallback(async (files) => {
    if (!projectId || !clientId) {
      console.error('Missing projectId or clientId for upload');
      return;
    }

    setUploading(true);
    const newImages = [];

    for (const file of files) {
      try {
        const fileId = crypto.randomUUID();
        setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));

        const onProgress = (progress) => {
          setUploadProgress(prev => ({ ...prev, [fileId]: progress }));
        };

        // Upload to appropriate folder based on type
        const result = await SupabaseStorageService.uploadFile(
          file,
          projectId,
          clientId,
          onProgress,
          null,
          type
        );

        if (result.error) {
          throw new Error(result.error);
        }

        // Modify upload path to be in model-planner subfolder
        // This is a simple approach - ideally the service would handle this
        newImages.push(result);

        setUploadProgress(prev => {
          const updated = { ...prev };
          delete updated[fileId];
          return updated;
        });

      } catch (error) {
        console.error('Error uploading image:', error);
        setErrors(prev => ({ ...prev, [file.name]: error.message }));
      }
    }

    setUploading(false);

    if (newImages.length > 0) {
      // Append new images to existing ones
      const updatedImages = [...images, ...newImages];
      onImagesChange(updatedImages);
    }
  }, [images, onImagesChange, projectId, clientId, type]);

  // Handle paste event
  useEffect(() => {
    const handlePaste = async (e) => {
      if (disabled || uploading) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const file = items[i].getAsFile();
          if (file) {
            imageFiles.push(file);
          }
        }
      }

      if (imageFiles.length > 0) {
        e.preventDefault();
        await uploadImages(imageFiles);
      }
    };

    const element = pasteAreaRef.current;
    if (element) {
      element.addEventListener('paste', handlePaste);
      return () => element.removeEventListener('paste', handlePaste);
    }
  }, [disabled, uploading, uploadImages]);

  const removeImage = (imageId) => {
    const updatedImages = images.filter(img => img.id !== imageId);
    onImagesChange(updatedImages);
  };

  const clearError = (filename) => {
    setErrors(prev => {
      const updated = { ...prev };
      delete updated[filename];
      return updated;
    });
  };

  return (
    <div className="image-paste-area-container">
      {/* Paste Zone */}
      <div
        ref={pasteAreaRef}
        className="image-paste-zone"
        tabIndex={0}
        style={{
          border: '1px solid #ccc',
          padding: '20px',
          textAlign: 'center',
          backgroundColor: disabled ? 'var(--surface-disabled)' : 'var(--surface-secondary)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          minHeight: '100px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Upload size={24} style={{ marginBottom: '10px', color: 'var(--text-secondary)' }} />
        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
          {disabled ? 'Upload disabled' : 'Click here and press Ctrl+V to paste images'}
        </p>
        {uploading && (
          <p style={{ margin: '10px 0 0 0', color: 'var(--text-secondary)', fontSize: '14px' }}>
            Uploading...
          </p>
        )}
      </div>

      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div style={{ marginTop: '10px' }}>
          {Object.entries(uploadProgress).map(([id, progress]) => (
            <div key={id} style={{ marginBottom: '5px' }}>
              <div style={{
                height: '4px',
                backgroundColor: 'var(--surface-hover)',
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${progress}%`,
                  backgroundColor: 'var(--text-secondary)',
                  transition: 'width 0.3s'
                }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error Messages */}
      {Object.keys(errors).length > 0 && (
        <div style={{ marginTop: '10px' }}>
          {Object.entries(errors).map(([filename, error]) => (
            <div
              key={filename}
              style={{
                padding: '8px',
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                marginBottom: '5px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <span style={{ fontSize: '14px', color: '#c00' }}>
                {filename}: {error}
              </span>
              <button
                onClick={() => clearError(filename)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0',
                  display: 'flex'
                }}
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Image Thumbnails */}
      {images.length > 0 && (
        <div style={{ marginTop: '15px' }}>
          <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', fontSize: '14px' }}>
            Uploaded Images ({images.length})
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: '10px'
          }}>
            {images.map((image) => (
              <div
                key={image.id}
                style={{
                  position: 'relative',
                  border: '1px solid #ccc',
                  padding: '5px',
                  backgroundColor: 'var(--surface-secondary)'
                }}
              >
                {/* Remove Button */}
                <button
                  onClick={() => removeImage(image.id)}
                  style={{
                    position: 'absolute',
                    top: '5px',
                    right: '5px',
                    background: 'var(--surface-secondary)',
                    border: '1px solid #ccc',
                    borderRadius: '0',
                    cursor: 'pointer',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Remove image"
                >
                  <X size={16} />
                </button>

                {/* Thumbnail */}
                <img
                  src={image.url}
                  alt={image.filename}
                  style={{
                    width: '100%',
                    height: '120px',
                    objectFit: 'cover',
                    display: 'block',
                    cursor: 'pointer'
                  }}
                  onClick={() => setPreviewImage(image)}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />

                {/* Fallback for failed image load */}
                <div style={{
                  display: 'none',
                  height: '120px',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'var(--surface-disabled)',
                  color: 'var(--text-secondary)'
                }}>
                  Image
                </div>

                {/* Filename */}
                <p style={{
                  margin: '5px 0 0 0',
                  fontSize: '12px',
                  color: 'var(--text-secondary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }} title={image.filename}>
                  {image.filename}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      <FilePreviewModalSimple
        file={previewImage}
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
      />
    </div>
  );
};

export default ImagePasteArea;
